import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Modal from "../common/Modal";
import type { Assignment } from "./types";

type DetailField = {
    label: string;
    value: ReactNode;
    show?: boolean;
};

type FormCard = {
    label: string;
    description: string;
    path: string;
    status: string;
    phase: "pre" | "post";
};

type AssignmentDetailViewProps = {
    assignment: Assignment;
    fields: DetailField[];
    filesSection?: ReactNode;
    canShowForms: boolean;
    showJsaCard: boolean;
    hasJsaData: boolean;
    jsaRequired: boolean;
    jsaStatus: string;
    preFormCards: FormCard[];
    jsaDone: boolean;
    preFormsDone: boolean;
    hasJobTagData: boolean;
    jobTagStatus: string;
    jobTagDone: boolean;
    postFormCards: FormCard[];
    showJsaModal: boolean;
    showJobTagModal: boolean;
    onOpenJsaModal: () => void;
    onCloseJsaModal: () => void;
    onOpenJobTagModal: () => void;
    onCloseJobTagModal: () => void;
    onDownloadJsa: () => void;
    onDownloadJobTag: () => void;
    onSubmitAssignment: () => void;
    onWithdrawAssignment: () => void;
};

export default function AssignmentDetailView({
    assignment,
    fields,
    filesSection,
    canShowForms,
    showJsaCard,
    hasJsaData,
    jsaRequired,
    jsaStatus,
    preFormCards,
    jsaDone,
    preFormsDone,
    hasJobTagData,
    jobTagStatus,
    jobTagDone,
    postFormCards,
    showJsaModal,
    showJobTagModal,
    onOpenJsaModal,
    onCloseJsaModal,
    onOpenJobTagModal,
    onCloseJobTagModal,
    onDownloadJsa,
    onDownloadJobTag,
    onSubmitAssignment,
    onWithdrawAssignment,
}: AssignmentDetailViewProps) {
    const jsaFields = [
        { label: "JSA Submit Time", value: assignment.jsaSubmitTime },
        { label: "JSA Save Time", value: assignment.jsaSaveTime },
        { label: "Template", value: assignment.jobSafetyAnalysisTemplate },
        { label: "Template Doc", value: assignment.jobSafetyAnalysisTemplateDoc },
        { label: "Nearest Medical Facility", value: assignment.nearestMedicalFacility },
    ].filter(field => field.value);
    const jobTagFields = [
        { label: "Travel Start", value: assignment.travelStartTime },
        { label: "Work Start", value: assignment.workStartTime },
        { label: "Had Lunch", value: assignment.hadLunch },
        { label: "Work End", value: assignment.workEndTime },
        { label: "Travel End", value: assignment.travelEndTime },
        { label: "Work Finished", value: assignment.workFinished },
        { label: "Work Required", value: assignment.workRequired },
        { label: "Work Performed", value: assignment.workPerformed },
        { label: "Additional Information", value: assignment.additionalInfo },
    ].filter(field => field.value);
    const jsaContent = normalizeJsaContent(assignment.jobSafetyAnalysisContent);
    const jsaContentDisplay = assignment.jobSafetyAnalysisContent
        ? (
            typeof assignment.jobSafetyAnalysisContent === "string"
                ? assignment.jobSafetyAnalysisContent
                : JSON.stringify(assignment.jobSafetyAnalysisContent, null, 2)
        )
        : "";
    const jsaOverviewFields = [
        { label: "Construct Smarter Project Name", value: jsaContent.pn || assignment.projectName },
        { label: "Job Location", value: jsaContent.loc || assignment.location },
        { label: "Nearest Medical Facility", value: jsaContent.loc2 || assignment.nearestMedicalFacility },
        { label: "Nearest Evacuation Point", value: jsaContent.loc3 },
        { label: "Nearest Eyewash / Fire Extinguisher / First Aid", value: jsaContent.loc4 },
    ].filter(field => field.value);
    const jsaCheckFields = [
        { label: "Valid USA Ticket?", value: jsaContent.check1 },
        { label: "Controlled potential water runoff for hydrovac excavation?", value: jsaContent.sel1 },
        { label: "Examined the entire site for hazards?", value: jsaContent.sel2 },
        { label: "Confined Space / Hot Work?", value: jsaContent.sel3 },
    ].filter(field => field.value);
    const jsaPpeSections = [
        { label: "Eye / Face", values: normalizeDisplayArray(jsaContent.eyeFace) },
        { label: "Hand / Gloves", values: normalizeDisplayArray(jsaContent.handGloves) },
        { label: "Foot", values: normalizeDisplayArray(jsaContent.foot) },
        { label: "Head", values: normalizeDisplayArray(jsaContent.head) },
        { label: "Gas Detector", values: normalizeDisplayArray(jsaContent.gasDetector) },
        { label: "Respiratory Protection", values: normalizeDisplayArray(jsaContent.respiratoryProtection) },
        { label: "Protective Clothing", values: normalizeDisplayArray(jsaContent.protectiveClothing) },
        { label: "Hearing", values: normalizeDisplayArray(jsaContent.hearing) },
        { label: "Fall Protection", values: normalizeDisplayArray(jsaContent.fallProtectionList) },
    ].filter(section => section.values.length);
    const jsaHazardControls = normalizeHazardControls(jsaContent.hazardControls);
    const jsaSifSections = normalizeSifSections(jsaContent.sif);
    const primaryJsaSignature = normalizeSignaturePreview(jsaContent.sign);
    const customerJsaSignatures = normalizeSignatureCollection(jsaContent.jobSafetyAnalysisSigns, jsaContent.signs);
    const internalJsaSignatures = normalizeSignatureCollection(jsaContent.jobSafetyAnalysisInternalSigns, jsaContent.iSigns);

    return (
        <div>
            <div className="ps-3 pt-3">
                <div className="row g-3 align-items-stretch">
                    <div className="col-12 col-xl-5">
                        <table className="table table-fixed mb-0">
                            <tbody className="noTableTopBorder">
                                {fields
                                    .filter(field => field.value && field.show !== false)
                                    .map((field, idx) => (
                                        <tr key={idx}>
                                            <th>{field.label}</th>
                                            <td>{field.value}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="col-12 col-xl-7">
                        <div className="row g-3 h-100">
                            <div className="col-12 col-md-6 d-flex">
                                <fieldset className="border rounded flex-grow-1 px-3">
                                    <legend className="fw-bold">Project Description</legend>
                                    <span className="cssNotes" dangerouslySetInnerHTML={{ __html: assignment.projectDescription || "" }} />
                                </fieldset>
                            </div>
                            <div className="col-12 col-md-6 d-flex">
                                <fieldset className="border rounded flex-grow-1 px-3">
                                    <legend className="fw-bold">Work Description</legend>
                                    <span className="cssNotes" dangerouslySetInnerHTML={{ __html: assignment.workDescription || "" }} />
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {canShowForms ? (
                <div className="px-3 pt-4 d-grid gap-3">
                    {showJsaCard ? (
                        <div className="border rounded p-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
                            <div>
                                <div className="fw-bold">JSA</div>
                                <div className="text-muted">
                                    {jsaRequired
                                        ? (hasJsaData ? "JSA data available." : "No JSA submitted yet.")
                                        : "JSA waived for this assignment."}
                                </div>
                                <div className="small mt-1">
                                    Status: <span className={jsaStatus === "Submitted" ? "text-success" : jsaStatus === "Saved" ? "text-warning" : "text-muted"}>
                                        {jsaStatus}
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {hasJsaData ? (
                                    <button type="button" className="btn btn-outline-primary" onClick={onOpenJsaModal}>
                                        View
                                    </button>
                                ) : null}
                                {assignment.jsaFileId ? (
                                    <button type="button" className="btn btn-primary" onClick={onDownloadJsa}>
                                        Download
                                    </button>
                                ) : null}
                                {jsaRequired ? (
                                    <Link to="JSA" className="btn btn-success">
                                        Edit
                                    </Link>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    {preFormCards.map(form => (
                        <FormCardItem key={form.path} form={form} />
                    ))}
                    {jsaDone && preFormsDone ? (
                        <div className="border rounded p-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
                            <div>
                                <div className="fw-bold">Job Tag</div>
                                <div className="text-muted">
                                    {hasJobTagData ? "Job tag data available." : "No job tag submitted yet."}
                                </div>
                                <div className="small mt-1">
                                    Status: <span className={jobTagStatus === "Submitted" ? "text-success" : jobTagStatus === "Saved" ? "text-warning" : "text-muted"}>
                                        {jobTagStatus}
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {hasJobTagData ? (
                                    <button type="button" className="btn btn-outline-primary" onClick={onOpenJobTagModal}>
                                        View
                                    </button>
                                ) : null}
                                {assignment.jobTagFileId ? (
                                    <button type="button" className="btn btn-primary" onClick={onDownloadJobTag}>
                                        DownLoad
                                    </button>
                                ) : null}
                                <Link to="JobTag" className="btn btn-success">
                                    Edit
                                </Link>
                            </div>
                        </div>
                    ) : null}
                    {jsaDone && jobTagDone ? postFormCards.map(form => (
                        <FormCardItem key={form.path} form={form} />
                    )) : null}
                </div>
            ) : null}
            {filesSection ? (
                <div className="p-3">
                    {filesSection}
                </div>
            ) : null}
            <div className="p-3 d-flex gap-3">
                {assignment.status === "Submitted" ? (
                    <button type="button" className="btn btn-warning" onClick={onWithdrawAssignment}>
                        Withdraw
                    </button>
                ) : canShowForms ? (
                    <button type="button" className="btn btn-primary" onClick={onSubmitAssignment}>
                        Submit
                    </button>
                ) : null}
                <Link children="Back" to=".." relative="path" className="btn btn-outline-secondary ms-auto" />
            </div>
            {showJsaModal ? (
                <Modal title="JSA Details" closeIt={onCloseJsaModal} width={900} maxWidth="95%">
                    <div className="py-3 d-grid gap-3">
                        {jsaFields.length ? (
                            <div className="row g-3">
                                {jsaFields.map((field, idx) => (
                                    <div key={idx} className={field.label.includes("Time") ? "col-12" : "col-12 col-md-6"}>
                                        <div className="border rounded h-100 px-3 py-2 bg-light">
                                            <div className="small text-muted text-uppercase fw-bold mb-1">{field.label}</div>
                                            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{field.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {jsaContentDisplay ? (
                            <div className="d-grid gap-3">
                                {jsaOverviewFields.length ? (
                                    <InfoSection title="Overview">
                                        <div className="row g-3 p-3">
                                            {jsaOverviewFields.map((field, idx) => (
                                                <LabeledValue key={idx} className="col-12 col-md-6" label={field.label} value={field.value} />
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {jsaCheckFields.length ? (
                                    <InfoSection title="Site Checks">
                                        <div className="row g-3 p-3">
                                            {jsaCheckFields.map((field, idx) => (
                                                <div key={idx} className="col-12 col-md-6">
                                                    <div className="small text-muted text-uppercase fw-bold mb-1">{field.label}</div>
                                                    <div className="fw-bold">{field.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {jsaPpeSections.length ? (
                                    <InfoSection title="PPE">
                                        <div className="row g-3 p-3">
                                            {jsaPpeSections.map((section, idx) => (
                                                <div key={idx} className="col-12 col-md-6">
                                                    <div className="small text-muted text-uppercase fw-bold mb-2">{section.label}</div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {section.values.map((value, valueIndex) => (
                                                            <span key={valueIndex} className="badge text-bg-light border text-dark fw-normal">
                                                                {value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {jsaHazardControls.length ? (
                                    <InfoSection title="Hazard Controls">
                                        <div className="d-grid gap-2 p-3">
                                            {jsaHazardControls.map((item, idx) => (
                                                <div key={idx} className="border rounded px-3 py-2 bg-light">
                                                    <div><span className="small text-muted me-2">Hazard</span><span className="fw-bold">{item.hazard}</span></div>
                                                    <div className="mt-1"><span className="small text-muted me-2">Control</span>{item.control}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {jsaSifSections.length ? (
                                    <InfoSection title="Serious Injury & Fatality Controls">
                                        <div className="d-grid gap-3 p-3">
                                            {jsaSifSections.map((section, idx) => (
                                                <div key={idx} className="border rounded px-3 py-3 bg-light">
                                                    <div className="fw-bold mb-2">{section.title}</div>
                                                    {section.inputHazard ? (
                                                        <div className="small mb-2"><span className="text-muted me-2">Hazard</span>{section.inputHazard}</div>
                                                    ) : null}
                                                    {section.hazards.map((hazard, hazardIdx) => (
                                                        <div key={hazardIdx} className={hazardIdx ? "mt-2" : ""}>
                                                            <div className="small text-muted fw-bold">{hazard.title}</div>
                                                            <div className="d-flex flex-wrap gap-2 mt-1">
                                                                {hazard.controls.map((control, controlIdx) => (
                                                                    <span key={controlIdx} className="badge text-bg-light border text-dark fw-normal">
                                                                        {control}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {section.inputControls ? (
                                                        <div className="small mt-2"><span className="text-muted me-2">Additional Controls</span>{section.inputControls}</div>
                                                    ) : null}
                                                </div>
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {primaryJsaSignature ? (
                                    <InfoSection title="Primary Signature">
                                        <div className="p-3">
                                            <SignaturePreview imageUrl={primaryJsaSignature.imageUrl} />
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {customerJsaSignatures.length ? (
                                    <InfoSection title="Customer Signatures">
                                        <div className="d-grid gap-3 p-3">
                                            {customerJsaSignatures.map((signature, idx) => (
                                                <div key={idx} className="border rounded px-3 py-3 bg-light">
                                                    <div className="d-flex flex-wrap gap-3 small mb-2">
                                                        {signature.name ? <div><span className="text-muted me-2">Name</span><span className="fw-bold">{signature.name}</span></div> : null}
                                                        {signature.date ? <div><span className="text-muted me-2">Date</span><span className="fw-bold">{signature.date}</span></div> : null}
                                                        {signature.role ? <div><span className="text-muted me-2">Role</span><span className="fw-bold">{signature.role}</span></div> : null}
                                                    </div>
                                                    <SignaturePreview imageUrl={signature.imageUrl} />
                                                </div>
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {internalJsaSignatures.length ? (
                                    <InfoSection title="Internal Signatures">
                                        <div className="d-grid gap-3 p-3">
                                            {internalJsaSignatures.map((signature, idx) => (
                                                <div key={idx} className="border rounded px-3 py-3 bg-light">
                                                    <div className="d-flex flex-wrap gap-3 small mb-2">
                                                        {signature.name ? <div><span className="text-muted me-2">Name</span><span className="fw-bold">{signature.name}</span></div> : null}
                                                        {signature.assignmentId ? <div><span className="text-muted me-2">Assignment</span><span className="fw-bold">{signature.assignmentId}</span></div> : null}
                                                        {signature.ppe ? <div><span className="text-muted me-2">PPE</span><span className="fw-bold">{signature.ppe}</span></div> : null}
                                                    </div>
                                                    <SignaturePreview imageUrl={signature.imageUrl} />
                                                </div>
                                            ))}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {!jsaOverviewFields.length && !jsaCheckFields.length && !jsaPpeSections.length && !jsaHazardControls.length && !jsaSifSections.length ? (
                                    <InfoSection title="JSA Content">
                                        <div
                                            className="px-3 py-3"
                                            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "420px", overflowY: "auto", background: "#fff" }}
                                        >
                                            {jsaContentDisplay}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {jsaContent.notes ? (
                                    <InfoSection title="Notes">
                                        <div className="px-3 py-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                            {jsaContent.notes}
                                        </div>
                                    </InfoSection>
                                ) : null}
                                {jsaContent.sel1 === "No" ? (
                                    <div className="alert alert-danger mb-0">
                                        Potential water runoff was marked as not controlled. Equipment damage report follow-up may be required.
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {assignment.jobSafetyAnalysisSignId ? (
                            <div className="border rounded px-3 py-2 bg-light d-flex align-items-center gap-3 flex-wrap">
                                <div>
                                    <span className="small text-muted me-2">Sign ID</span>
                                    <span className="fw-bold">{assignment.jobSafetyAnalysisSignId}</span>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </Modal>
            ) : null}
            {showJobTagModal ? (
                <Modal title="Job Tag Details" closeIt={onCloseJobTagModal} width={900} maxWidth="95%">
                    <div className="py-3">
                        <table className="table table-fixed mb-0">
                            <tbody className="noTableTopBorder">
                                {assignment.coords ? (
                                    <tr>
                                        <th>Coordinates</th>
                                        <td>
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.coords.split(",").slice(0, 2).join(","))}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {assignment.coords.split(",").slice(0, 2).join(", ")}
                                            </a>
                                            {assignment.coords.split(",")[2] ? (
                                                <div className="text-muted small mt-1">
                                                    Accuracy: {assignment.coords.split(",")[2]} meters
                                                </div>
                                            ) : null}
                                        </td>
                                    </tr>
                                ) : null}
                                {jobTagFields.map((field, idx) => (
                                    <tr key={idx}>
                                        <th style={{ width: "220px" }}>{field.label}</th>
                                        <td style={{ whiteSpace: "pre-wrap" }}>{field.value}</td>
                                    </tr>
                                ))}
                                {assignment.sign ? (
                                    <tr>
                                        <th>Signature</th>
                                        <td>
                                            <img
                                                src={assignment.sign}
                                                alt="Job tag signature"
                                                className="img-fluid border rounded"
                                                style={{ maxWidth: "420px", background: "#fff" }}
                                            />
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}

function FormCardItem({ form }: { form: FormCard }) {
    return (
        <div className="border rounded p-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <div>
                <div className="fw-bold">{form.label}</div>
                <div className="text-muted">{form.description}</div>
                <div className="small mt-1">
                    Status: <span className={form.status === "Submitted" ? "text-success" : form.status === "Saved" ? "text-warning" : "text-muted"}>
                        {form.status || "Not started"}
                    </span>
                </div>
            </div>
            <Link to={form.path} className="btn btn-success">
                Edit
            </Link>
        </div>
    );
}

function InfoSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="border rounded overflow-hidden">
            <div className="px-3 py-2 border-bottom bg-light fw-bold">{title}</div>
            {children}
        </div>
    );
}

function LabeledValue({ label, value, className = "" }: { label: string; value: ReactNode; className?: string }) {
    return (
        <div className={className}>
            <div className="small text-muted text-uppercase fw-bold mb-1">{label}</div>
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value}</div>
        </div>
    );
}

function SignaturePreview({ imageUrl }: { imageUrl?: string }) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext("2d");
        if (!context) return;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.lineWidth = 2;
        context.strokeStyle = "#111827";
        context.lineCap = "round";
        context.lineJoin = "round";

        if (imageUrl) {
            const image = new Image();
            image.onload = () => {
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.fillStyle = "#ffffff";
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.drawImage(image, 0, 0, canvas.width, canvas.height);
            };
            image.src = imageUrl;
        }
    }, [imageUrl]);

    if (!imageUrl) return null;

    return (
        <canvas
            ref={canvasRef}
            width={420}
            height={160}
            className="border rounded bg-white img-fluid"
            style={{ maxWidth: "100%" }}
        />
    );
}

function normalizeJsaContent(value: any) {
    if (!value) return {};
    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch {
            return {};
        }
    }
    return value;
}

function normalizeDisplayArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
        } catch {
            return value.split(",").map(item => item.trim()).filter(Boolean);
        }
    }
    return [];
}

function normalizeHazardControls(value: any) {
    const parsed = typeof value === "string" ? normalizeJsaContent(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed
        .map(item => ({
            hazard: item?.others ? item?.otherHazard : item?.hazard,
            control: item?.control,
        }))
        .filter(item => item.hazard || item.control);
}

function normalizeSifSections(value: any) {
    const parsed = typeof value === "string" ? normalizeJsaContent(value) : value;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
    return Object.entries(parsed)
        .map(([title, section]: [string, any]) => ({
            title,
            inputHazard: section?.inputHazard || "",
            inputControls: section?.inputControls || "",
            hazards: Object.entries(section?.content || {})
                .map(([hazardTitle, controls]: [string, any]) => ({
                    title: hazardTitle,
                    controls: Array.isArray(controls) ? controls.map(String).filter(Boolean) : [],
                }))
                .filter(hazard => hazard.controls.length),
        }))
        .filter(section => section.inputHazard || section.inputControls || section.hazards.length);
}

function normalizeSignaturePreview(imageValue: any) {
    const imageUrl = typeof imageValue === "string" && imageValue ? imageValue : "";
    if (!imageUrl) return null;
    return { imageUrl };
}

function normalizeSignatureCollection(strokeValue: any, imageValue: any) {
    const strokeItems = normalizeSignatureObjects(strokeValue);
    const imageItems = normalizeImageList(imageValue);
    const maxLength = Math.max(strokeItems.length, imageItems.length);
    return Array.from({ length: maxLength }, (_, index) => {
        const strokeItem = strokeItems[index] || {};
        const imageUrl = imageItems[index] || "";
        return {
            imageUrl,
            name: strokeItem.name || strokeItem.laborName || "",
            date: strokeItem.date || "",
            role: strokeItem.role || "",
            assignmentId: strokeItem.assignmentId || "",
            ppe: strokeItem.ppe || "",
        };
    }).filter(item => item.imageUrl);
}

function normalizeSignatureObjects(value: any) {
    const parsed = typeof value === "string" ? normalizeJsaContent(value) : value;
    return Array.isArray(parsed) ? parsed : [];
}

function normalizeImageList(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : value ? [value] : [];
        } catch {
            return value ? [value] : [];
        }
    }
    return [];
}

export function getRequiredFormStatus(forms: {formName: string, status: "Submitted" | "Saved"}[], key: string) {
    const form = forms.find(form => form.formName === key);
    if (!form) return "Created";
    return form.status;
}
